import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "dialog__header flex flex-column w-8" }
const _hoisted_2 = ["textContent"]

import { computed, ref, toRefs, watch } from 'vue';
import Dialog from 'primevue/dialog';
import TreeTable from 'primevue/treetable';
import { TreeNode } from 'primevue/treenode';
import { useFlattenOrganizations } from '@/composables';
import { OrganizationsInterface } from '@/types';
import { EMIT } from '@/enum';

interface AddItemsInterface {
  name: string;
  value: number;
}

interface PropsInterface {
  items?: OrganizationsInterface[] | [];
  title: string;
  className?: string | null;
  multiSelection: boolean;
  listHeaderText?: string;
  cancel?: () => void;
  save?: (items: AddItemsInterface[]) => void;
  searchable?: boolean;
  searchProperty?: string;
  loading?: boolean;
  selectedItemsData?: Record<number, Record<string, boolean>>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AddOrganizationModal',
  props: {
    items: {},
    title: {},
    className: {},
    multiSelection: { type: Boolean },
    listHeaderText: {},
    cancel: { type: Function },
    save: { type: Function },
    searchable: { type: Boolean },
    searchProperty: {},
    loading: { type: Boolean },
    selectedItemsData: {}
  },
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;
const { items, title, selectedItemsData } = toRefs(props);
const selectedItems = ref<Record<number, Record<string, boolean>>>(selectedItemsData?.value ?? {});
const modalVisible = ref(true);
const listHeaderText = props.listHeaderText || 'Name';
const flatOrgs = useFlattenOrganizations(items?.value as TreeNode[]);

const selectedOrganizations = computed(() => {
  return Object.keys(selectedItems.value).map((organizationId) => Number(organizationId));
});

const selectionMode = computed(() => {
  return props.multiSelection ? 'checkbox' : 'single';
});

const save = () => {
  emit(EMIT.SAVE, selectedOrganizations.value);

  selectedItems.value = {};
  modalVisible.value = false;
  return;
};

const cancel = () => {
  emit(EMIT.CANCEL);
  selectedItems.value = {};
  modalVisible.value = false;
  return;
};

const onSearch = () => {
  selectedItems.value = {};
};

/*
  The tree checkboxes behave in a way that does not match the behavior we desire. To skirt around
  these issues, this watch statement:
  #1 prevents things being checked after something has been removed.
    There should never be a case where something is unchecked and something is also added.
  #2 prevents the deselection of a parent checkbox that has no children selected
*/
watch(selectedItems, (newVal, oldVal) => {
  if (!selectionMode.value) {
    for (const [key, value] of Object.entries(newVal)) {
      if (!value.checked && !value.partialChecked) {
        if (oldVal[Number(key)]?.partialChecked) {
          delete newVal[Number(key)];
        }
      }
    }
    const newSelections = Object.keys(newVal);
    const oldSelections = Object.keys(oldVal);
    const selectionsRemoved = oldSelections.filter((selection) => !newSelections.includes(selection));
    const selectionsAdded = newSelections.filter((selection) => !oldSelections.includes(selection));
    // #1
    if (selectionsRemoved.length && selectionsAdded.length) {
      selectionsAdded.forEach((selection) => {
        delete selectedItems.value[Number(selection)];
      });
    }
    // #2
    if (selectionsRemoved.length > 1) {
      selectionsRemoved.forEach((selection) => {
        if (oldVal[Number(selection)].partialChecked) {
          selectedItems.value[Number(selection)] = { checked: true, partialChecked: false };
        }
      });
    }
  }
});

const checkSelectedItems = (data: TreeNode) => {
  let orgData = flatOrgs.find((org) => org?.id === data?.key);
  if (!orgData?.parent_id) return;
  let hasParent = true;
  while (hasParent) {
    orgData = flatOrgs.find((org) => org?.id === orgData?.parent_id);

    if (orgData?.id) delete selectedItems.value[Number(orgData?.id)];
    hasParent = orgData?.parent_id ? true : false;
  }
};

const filters = ref({ name: '' });

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_Column = _resolveComponent("Column")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createBlock(_unref(Dialog), {
    visible: modalVisible.value,
    "onUpdate:visible": _cache[3] || (_cache[3] = ($event: any) => ((modalVisible).value = $event)),
    class: "dialog__container",
    modal: true,
    draggable: false,
    onHide: cancel
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h2", {
          class: "mb-3",
          textContent: _toDisplayString(props.title)
        }, null, 8, _hoisted_2),
        _createVNode(_component_IconField, { "icon-position": "left" }, {
          default: _withCtx(() => [
            _createVNode(_component_InputIcon, null, {
              default: _withCtx(() => [
                _createVNode(_component_Icon, {
                  name: "search",
                  class: "text-lg"
                })
              ]),
              _: 1
            }),
            _createVNode(_component_InputText, {
              modelValue: filters.value.name,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.name) = $event)),
              class: "w-full",
              placeholder: "Search Organizations",
              onInput: onSearch
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        })
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_AppButton, {
        type: "secondary",
        text: "Cancel",
        onClick: cancel
      }),
      _createVNode(_component_AppButton, {
        type: "primary",
        text: _unref(title),
        disabled: !Object.keys(selectedItems.value).length,
        onClick: save
      }, null, 8, ["text", "disabled"])
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(TreeTable), {
        filters: filters.value,
        "onUpdate:filters": _cache[1] || (_cache[1] = ($event: any) => ((filters).value = $event)),
        selectionKeys: selectedItems.value,
        "onUpdate:selectionKeys": _cache[2] || (_cache[2] = ($event: any) => ((selectedItems).value = $event)),
        value: _unref(items),
        "meta-key-selection": !__props.multiSelection,
        "global-filter-fields": ['name'],
        scrollable: true,
        "selection-mode": selectionMode.value,
        "responsive-layout": "scroll",
        "scroll-height": "flex",
        onNodeSelect: checkSelectedItems
      }, {
        empty: _withCtx(() => [...(_cache[4] || (_cache[4] = [
          _createTextVNode("No organization to be added", -1)
        ]))]),
        default: _withCtx(() => [
          (props.searchable)
            ? (_openBlock(), _createBlock(_component_Column, {
                key: 0,
                expander: true,
                field: "name",
                header: _unref(listHeaderText),
                sortable: ""
              }, null, 8, ["header"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["filters", "selectionKeys", "value", "meta-key-selection", "selection-mode"])
    ]),
    _: 1
  }, 8, ["visible"]))
}
}

})